/**
 * UserManagementScreen — UserGroup → Role → Users + Privileges Grid
 * Features: Lazy tree, right-click context, Edit modal, Levels as tooltip, privilege grid
 * State: React Context (UserTreeContext)
 * Data: 100% from API
 */
import React, { useState, useEffect, useCallback, useMemo, memo, useRef } from 'react';
import { ChevronRight, ChevronDown, Loader, Search, RotateCcw, Filter, Users, Shield, UserCheck, Lock, Activity, Edit3, X, Check, Info, MoreVertical } from 'lucide-react';
import ERPAdvancedGrid from '../../components/erp/ERPAdvancedGrid';
import { UserTreeProvider, useUserTree } from '../../context/UserTreeContext';
import { erpToast } from '../../components/erp/ERPToast';
import { FormHelp } from '../../components/help-engine/FormHelp';
import { HelpInfo } from '../../components/help-engine/HelpInfo';
import '../../styles/erp-theme.css';

/* ═══════════════════════════════════════════════════
   CONTEXT MENU
═══════════════════════════════════════════════════ */
const ContextMenu = ({ x, y, items, onClose }) => {
  const ref = useRef(null);
  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [onClose]);
  const menuStyle = {
    position: 'fixed', top: Math.min(y, window.innerHeight - 200), left: Math.min(x, window.innerWidth - 200),
    zIndex: 1300, minWidth: 180, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8,
    boxShadow: '0 12px 32px rgba(0,0,0,0.12), 0 2px 6px rgba(0,0,0,0.08)', padding: '4px 0',
    animation: 'erp-dropdown-in 120ms ease-out',
  };
  return (
    <div ref={ref} style={menuStyle} data-testid="context-menu">
      {items.map((item, i) => item.divider ? (
        <div key={item.id || `divider-${i}`} style={{ height: 1, background: '#f1f5f9', margin: '4px 8px' }} />
      ) : (
        <button key={item.id || `item-${i}`} onClick={() => { item.action(); onClose(); }}
          style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', padding: '8px 14px', fontSize: 12, fontWeight: 500, background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left', color: item.danger ? '#dc2626' : '#1e293b', transition: 'background 0.1s' }}
          onMouseEnter={e => { if (!e.currentTarget) return;  e.currentTarget.style.background = item.danger ? '#fef2f2' : '#f1f5f9'; }}
          onMouseLeave={e => { if (!e.currentTarget) return;  e.currentTarget.style.background = 'none'; }}
          data-testid={`ctx-${item.id}`}>
          <span style={{ width: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', color: item.danger ? '#ef4444' : '#64748b' }}>{item.icon}</span>
          {item.label}
        </button>
      ))}
    </div>
  );
};

/* ═══════════════════════════════════════════════════
   EDIT USER MODAL
═══════════════════════════════════════════════════ */
const EditUserModal = ({ user, onClose, onSave, groups, roles }) => {
  const [form, setForm] = useState({
    UserName: user?.UserName || '',
    GroupId: user?.GroupId || '',
    RoleId: user?.RoleId || '',
    Branchid: user?.Branchid || '',
    Status: user?.Status || 'S',
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleSave = async () => {
    setSaving(true);
    const ok = await onSave(user.Userid, form);
    setSaving(false);
    if (ok) { erpToast.success('User updated'); onClose(); } else erpToast.error('Update failed');
  };

  return (
    <div className="erp-modal-overlay" data-testid="edit-user-modal">
      <div style={{ background: '#fff', borderRadius: 8, width: 480, boxShadow: '0 10px 40px rgba(0,0,0,0.2)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 16px', borderBottom: '1px solid #e5e7eb', background: '#f9fafb' }}>
          <span style={{ fontWeight: 700, fontSize: 13 }}>Edit User: {user?.Userid}</span>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={16} /></button>
        </div>
        <div style={{ padding: 16 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', display: 'block', marginBottom: 4 }}>User Name<HelpInfo controlId="UserName" controlName="User Name" /></label>
              <input value={form.UserName} onChange={e => set('UserName', e.target.value)} className="erp-input" style={{ fontSize: 12 }} data-testid="edit-username" data-help-control="UserName" />
            </div>
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', display: 'block', marginBottom: 4 }}>Group<HelpInfo controlId="GroupId" controlName="Group" /></label>
              <select value={form.GroupId} onChange={e => set('GroupId', e.target.value)} className="erp-input erp-select" style={{ fontSize: 12 }} data-testid="edit-group" data-help-control="GroupId">
                <option value="">--Select--</option>
                {groups.map(g => <option key={g.UGroupId} value={g.UGroupId}>{g.UGroupName}</option>)}
              </select>
            </div>
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', display: 'block', marginBottom: 4 }}>Role<HelpInfo controlId="RoleId" controlName="Role" /></label>
              <select value={form.RoleId} onChange={e => set('RoleId', e.target.value)} className="erp-input erp-select" style={{ fontSize: 12 }} data-testid="edit-role" data-help-control="RoleId">
                <option value="">--Select--</option>
                {roles.map(r => <option key={r.URoleId} value={r.URoleId}>{r.URoleName}</option>)}
              </select>
            </div>
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', display: 'block', marginBottom: 4 }}>Status<HelpInfo controlId="Status" controlName="Status" /></label>
              <select value={form.Status} onChange={e => set('Status', e.target.value)} className="erp-input erp-select" style={{ fontSize: 12 }} data-testid="edit-status" data-help-control="Status">
                <option value="S">Active</option><option value="I">Inactive</option>
              </select>
            </div>
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
            <button onClick={onClose} className="erp-btn erp-btn-ghost"><X size={12} /> Cancel</button>
            <button onClick={handleSave} disabled={saving} className="erp-btn erp-btn-primary"><Check size={12} /> {saving ? 'Saving...' : 'Save'}</button>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════
   EDIT GROUP/ROLE MODAL
═══════════════════════════════════════════════════ */
const EditNodeModal = ({ node, onClose, onSave }) => {
  const [name, setName] = useState(node?.raw?.UGroupName || node?.raw?.URoleName || '');
  const [desc, setDesc] = useState(node?.raw?.UGroupDescription || node?.raw?.URoleDescription || '');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    let ok;
    if (node.type === 'group') ok = await onSave(node.raw.UGroupId, { UGroupName: name, UGroupDescription: desc });
    else ok = await onSave(node.raw.URoleId, { URoleName: name, URoleDescription: desc, UGroupId: node.raw.UGroupId });
    setSaving(false);
    if (ok) { erpToast.success(`${node.type} updated`); onClose(); } else erpToast.error('Update failed');
  };

  return (
    <div className="erp-modal-overlay" data-testid="edit-node-modal">
      <div style={{ background: '#fff', borderRadius: 8, width: 420, boxShadow: '0 10px 40px rgba(0,0,0,0.2)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 16px', borderBottom: '1px solid #e5e7eb', background: '#f9fafb' }}>
          <span style={{ fontWeight: 700, fontSize: 13 }}>Edit {node?.type}: {node?.name}</span>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={16} /></button>
        </div>
        <div style={{ padding: 16 }}>
          <div style={{ marginBottom: 12 }}>
            <label style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', display: 'block', marginBottom: 4 }}>Name<HelpInfo controlId="Name" controlName="Name" /></label>
            <input value={name} onChange={e => setName(e.target.value)} className="erp-input" style={{ fontSize: 12 }} autoFocus data-testid="edit-name" data-help-control="Name" />
          </div>
          <div style={{ marginBottom: 12 }}>
            <label style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', display: 'block', marginBottom: 4 }}>Description<HelpInfo controlId="Description" controlName="Description" /></label>
            <input value={desc} onChange={e => setDesc(e.target.value)} className="erp-input" style={{ fontSize: 12 }} data-testid="edit-desc" data-help-control="Description" />
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
            <button onClick={onClose} className="erp-btn erp-btn-ghost"><X size={12} /> Cancel</button>
            <button onClick={handleSave} disabled={saving} className="erp-btn erp-btn-primary"><Check size={12} /> {saving ? 'Saving...' : 'Save'}</button>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════
   TREE NODE (with ellipsis action button)
═══════════════════════════════════════════════════ */
const TreeNode = memo(({ node, depth, expanded, onToggle, onSelect, selectedId, onContext }) => {
  const isExp = expanded.has(node.id);
  const isSelected = node.id === selectedId;
  const isLeaf = !node.hasChildren;
  const children = node._children || [];
  const indent = 12 + depth * 16;
  const colors = ['#7c3aed', '#1e40af', '#374151'];
  const nodeColor = isSelected ? '#1d4ed8' : (colors[depth] || '#4b5563');
  const [hovered, setHovered] = useState(false);

  return (
    <div>
      <div
        onClick={() => { if (!isLeaf) onToggle(node.id); onSelect(node); }}
        onContextMenu={e => { e.preventDefault(); e.stopPropagation(); onContext(e, node); }}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        style={{ padding: `4px 4px 4px ${indent}px`, fontSize: depth === 0 ? 13 : 12, fontWeight: depth === 0 ? 700 : (depth === 1 ? 600 : (isSelected ? 600 : 400)), cursor: 'pointer', color: nodeColor, display: 'flex', alignItems: 'center', gap: 4, background: isSelected ? '#dbeafe' : hovered ? '#f3f4f6' : 'transparent', borderRadius: 4, margin: '0 4px', transition: 'background 0.1s' }}
        data-testid={`tree-node-${node.id}`}
      >
        {!isLeaf && (isExp ? <ChevronDown size={depth === 0 ? 13 : 11} style={{ flexShrink: 0 }} /> : <ChevronRight size={depth === 0 ? 13 : 11} style={{ flexShrink: 0 }} />)}
        {isLeaf && <span style={{ width: 11, flexShrink: 0 }} />}
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>{node.name}</span>
        {node.childCount > 0 && <span style={{ fontSize: 9, color: '#9ca3af', flexShrink: 0 }}>({node.childCount})</span>}
        <button
          onClick={e => { e.stopPropagation(); onContext(e, node); }}
          style={{ opacity: hovered || isSelected ? 1 : 0, flexShrink: 0, width: 20, height: 20, display: 'flex', alignItems: 'center', justifyContent: 'center', border: 'none', borderRadius: 4, background: 'transparent', cursor: 'pointer', color: '#6b7280', transition: 'opacity 0.15s, background 0.1s' }}
          onMouseEnter={e => { if (!e.currentTarget) return;  e.currentTarget.style.background = '#e5e7eb'; }}
          onMouseLeave={e => { if (!e.currentTarget) return;  e.currentTarget.style.background = 'transparent'; }}
          data-testid={`tree-action-${node.id}`}
          title="Actions"
        >
          <MoreVertical size={14} />
        </button>
      </div>
      {isExp && children.map(child => (
        <TreeNode key={child.id} node={child} depth={depth + 1} expanded={expanded} onToggle={onToggle} onSelect={onSelect} selectedId={selectedId} onContext={onContext} />
      ))}
    </div>
  );
});

/* ═══════════════════════════════════════════════════
   MAIN SCREEN (inner)
═══════════════════════════════════════════════════ */
const UserManagementInner = ({ onBack }) => {
  const ctx = useUserTree();
  const {
    groups, roles, users, levels, dataLoading, loadAll,
    expanded, selectedNode, treeSearch, setTreeSearch, toggleNode, selectNode,
    page, setPage, pageSize, setPageSize, sortField, setSortField, sortDir, setSortDir, filters, setFilters,
    searchText, setSearchText, statusFilter, setStatusFilter, roleFilter, setRoleFilter,
    selectedUserPrivs, privsLoading, editUser, editGroup, editRole, levelMap, resetFilters,
  } = ctx;

  const [ctxMenu, setCtxMenu] = useState(null);
  const [editNodeModal, setEditNodeModal] = useState(null);
  const [editUserModal, setEditUserModal] = useState(null);

  useEffect(() => { loadAll(); }, [loadAll]);

  // Build tree: Group → Role → Users
  const treeNodes = useMemo(() => {
    return groups
      .filter(g => !treeSearch || g.UGroupName?.toLowerCase().includes(treeSearch.toLowerCase()))
      .map(g => {
        const groupRoles = roles.filter(r => r.UGroupId === g.UGroupId);
        const roleNodes = groupRoles.map(r => {
          const roleUsers = users.filter(u => u.GroupId === g.UGroupId);
          return { id: `role-${r.URoleId}`, name: r.URoleName, type: 'role', raw: r, hasChildren: roleUsers.length > 0, childCount: roleUsers.length,
            _children: roleUsers.map(u => ({ id: `user-${u.Userid}`, name: u.UserName, type: 'user', raw: u, hasChildren: false, childCount: 0, _children: [] })) };
        });
        const groupUsers = users.filter(u => u.GroupId === g.UGroupId);
        return { id: `group-${g.UGroupId}`, name: g.UGroupName, type: 'group', raw: g, hasChildren: roleNodes.length > 0 || groupUsers.length > 0, childCount: groupUsers.length, _children: roleNodes };
      });
  }, [groups, roles, users, treeSearch]);

  const handleTreeContext = useCallback((e, node) => {
    const items = [{ id: 'edit', label: 'Edit', icon: <Edit3 size={12} />, action: () => {
      if (node.type === 'user') setEditUserModal(node.raw);
      else setEditNodeModal(node);
    }}];
    setCtxMenu({ x: e.clientX, y: e.clientY, items });
  }, []);

  // Grid data: users filtered by selected tree node
  const gridData = useMemo(() => {
    let items = users;
    if (selectedNode) {
      if (selectedNode.type === 'group') items = items.filter(u => u.GroupId === selectedNode.raw.UGroupId);
      else if (selectedNode.type === 'role') items = items.filter(u => u.GroupId === selectedNode.raw.UGroupId);
      else if (selectedNode.type === 'user') items = items.filter(u => u.Userid === selectedNode.raw.Userid);
    }
    if (searchText) items = items.filter(u => (u.UserName || '').toLowerCase().includes(searchText.toLowerCase()) || (u.Userid || '').toLowerCase().includes(searchText.toLowerCase()));
    if (statusFilter) items = items.filter(u => u.Status === statusFilter);
    if (roleFilter) items = items.filter(u => u.RoleId === roleFilter);
    if (sortField) items = [...items].sort((a, b) => { const av = String(a[sortField] || ''), bv = String(b[sortField] || ''); return sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av); });
    return items;
  }, [users, selectedNode, searchText, statusFilter, roleFilter, sortField, sortDir]);

  const pageData = gridData.slice((page - 1) * pageSize, page * pageSize);

  // Enrich with level tooltip data
  const enrichedData = useMemo(() => pageData.map(u => {
    const lvl = levelMap[u.Userid];
    return { ...u,
      LevelDisplay: lvl ? `${lvl.Levelofuser || '-'} (${lvl.RangeType || ''} ${lvl.FromAmount || 0}-${lvl.ToAmount || 0})` : '-',
      LevelTooltip: lvl ? `Level: ${lvl.Levelofuser}\nRange: ${lvl.RangeType} ${lvl.FromAmount}-${lvl.ToAmount}\nStatus: ${lvl.Status}` : 'No level assigned',
      LoginStatusDisplay: u.LoginStatus || '-',
      GroupDisplay: u.GroupName || u.GroupId || '-',
      RoleDisplay: u.RoleName || u.RoleId || '-',
    };
  }), [pageData, levelMap]);

  const kpi = useMemo(() => ({
    total: users.length,
    active: users.filter(u => u.Status === 'S').length,
    loggedIn: users.filter(u => u.LoginStatus === 'Login').length,
    locked: users.filter(u => u.LoginStatus === 'Locked').length,
    groups: groups.length,
  }), [users, groups]);

  const COLUMNS = [
    { field: 'Userid', header: 'User ID', width: 90 },
    { field: 'UserName', header: 'User Name', width: 150 },
    { field: 'GroupDisplay', header: 'Group', width: 120 },
    { field: 'RoleDisplay', header: 'Role', width: 130 },
    { field: 'BranchName', header: 'Branch', width: 120 },
    { field: 'LevelDisplay', header: 'Level', width: 140 },
    { field: 'LoginStatusDisplay', header: 'Login Status', width: 100 },
    { field: 'Status', header: 'Status', width: 80 },
  ];

  const handleSort = (f) => { if (sortField === f) setSortDir(d => d === 'asc' ? 'desc' : 'asc'); else { setSortField(f); setSortDir('asc'); } setPage(1); };

  return (
    <FormHelp formName="UserManagement">
    <div style={{ display: 'flex', height: '100%' }} data-testid="user-management-screen" data-dev-component="UserManagement" data-dev-file="screens/system/UserManagementScreen.js">
      {/* LEFT: Tree */}
      <div style={{ width: 260, flexShrink: 0, borderRight: '1px solid var(--erp-border)', display: 'flex', flexDirection: 'column', background: '#fafbfc' }}>
        <div style={{ padding: '8px', borderBottom: '1px solid var(--erp-border)' }}>
          <div style={{ position: 'relative' }}>
            <Search size={12} style={{ position: 'absolute', left: 6, top: 7, color: '#9ca3af' }} />
            <input value={treeSearch} onChange={e => setTreeSearch(e.target.value)} placeholder="Search groups/roles..." className="erp-input" style={{ paddingLeft: 24, fontSize: 11, height: 28, width: '100%' }} data-testid="tree-search" />
          </div>
        </div>
        <div style={{ flex: 1, overflow: 'auto' }} data-testid="user-tree">
          {dataLoading ? (
            <div style={{ padding: 16, textAlign: 'center', color: '#9ca3af', fontSize: 12 }}><Loader size={16} className="animate-spin" style={{ display: 'inline', marginRight: 6 }} />Loading...</div>
          ) : (
            <>
              <div style={{ padding: '6px 8px', fontSize: 10, fontWeight: 800, color: '#7c3aed', textTransform: 'uppercase', letterSpacing: 1, borderBottom: '1px solid #e5e7eb', background: '#f9fafb' }}>User Groups</div>
              {treeNodes.map(node => (
                <TreeNode key={node.id} node={node} depth={0} expanded={expanded} onToggle={toggleNode} onSelect={selectNode} selectedId={selectedNode?.id} onContext={handleTreeContext} />
              ))}
            </>
          )}
        </div>
        <div style={{ padding: '4px 8px', borderTop: '1px solid var(--erp-border)', fontSize: 9, color: '#9ca3af' }}>
          {groups.length} groups | {roles.length} roles | {users.length} users
        </div>
      </div>

      {/* RIGHT: Filters + KPI + Grid + Privileges */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        {/* Filter Row */}
        <div style={{ padding: '6px 12px', borderBottom: '1px solid var(--erp-border)', display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap', background: 'var(--erp-surface)' }}>
          <span style={{ fontSize: 14, fontWeight: 800, color: 'var(--erp-primary)', marginRight: 'auto', minWidth: 100 }}>{selectedNode ? selectedNode.name : 'All Users'}</span>
          <div style={{ position: 'relative' }}>
            <Search size={11} style={{ position: 'absolute', left: 6, top: 7, color: '#9ca3af' }} />
            <input value={searchText} onChange={e => { setSearchText(e.target.value); setPage(1); }} placeholder="Search..." className="erp-input" style={{ paddingLeft: 22, height: 24, fontSize: 10, width: 140, borderRadius: 12 }} data-testid="grid-search" />
          </div>
          <select value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(1); }} className="erp-input erp-select" style={{ height: 24, fontSize: 10, width: 90, borderRadius: 12 }}>
            <option value="">All Status</option><option value="S">Active</option><option value="I">Inactive</option>
          </select>
          <select value={roleFilter} onChange={e => { setRoleFilter(e.target.value); setPage(1); }} className="erp-input erp-select" style={{ height: 24, fontSize: 10, width: 120, borderRadius: 12 }}>
            <option value="">All Roles</option>{roles.map(r => <option key={r.URoleId} value={r.URoleId}>{r.URoleName}</option>)}
          </select>
          <button onClick={resetFilters} className="erp-btn erp-btn-ghost" style={{ height: 24, fontSize: 10, padding: '0 8px', borderRadius: 12 }}><RotateCcw size={10} /> Reset</button>
        </div>

        {/* KPI */}
        <div style={{ display: 'flex', gap: 8, padding: '8px 12px', borderBottom: '1px solid var(--erp-border)', background: 'var(--erp-surface-alt)' }}>
          {[
            { label: 'Total Users', value: kpi.total, color: '#1e40af', icon: Users },
            { label: 'Active', value: kpi.active, color: '#15803d', icon: UserCheck },
            { label: 'Logged In', value: kpi.loggedIn, color: '#0369a1', icon: Activity },
            { label: 'Locked', value: kpi.locked, color: '#dc2626', icon: Lock },
            { label: 'Groups', value: kpi.groups, color: '#7c3aed', icon: Shield },
          ].map(tile => (
            <div key={tile.label} style={{ flex: 1, padding: '6px 10px', borderRadius: 6, border: '1px solid var(--erp-border)', background: 'var(--erp-surface)', minWidth: 0 }}>
              <div style={{ fontSize: 9, color: 'var(--erp-text-muted)', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 3 }}><tile.icon size={10} />{tile.label}</div>
              <div style={{ fontSize: 16, fontWeight: 700, fontFamily: 'var(--erp-font-mono)', color: tile.color, marginTop: 2 }}>{tile.value}</div>
            </div>
          ))}
        </div>

        {/* Privilege summary for selected user */}
        {selectedNode?.type === 'user' && (
          <div style={{ padding: '6px 12px', borderBottom: '1px solid var(--erp-border)', background: '#fefce8', fontSize: 11 }}>
            {privsLoading ? <span><Loader size={11} className="animate-spin" style={{ display: 'inline', marginRight: 4 }} />Loading privileges...</span> : (
              <><strong>Privileges for {selectedNode.name}:</strong> {selectedUserPrivs.length} screen(s)
                {selectedUserPrivs.length > 0 && <span style={{ marginLeft: 8, color: '#6b7280' }}>{selectedUserPrivs.slice(0, 8).map(p => p.ScreenId).join(', ')}{selectedUserPrivs.length > 8 ? ` +${selectedUserPrivs.length - 8} more` : ''}</span>}
              </>
            )}
          </div>
        )}

        {/* Grid */}
        <div style={{ flex: 1, overflow: 'hidden' }}>
          <ERPAdvancedGrid
            title="" columns={COLUMNS} data={enrichedData} total={gridData.length}
            idField="Userid" nameField="UserName" page={page} pageSize={pageSize}
            onPageChange={setPage} onPageSizeChange={s => { setPageSize(s); setPage(1); }}
            sortField={sortField} sortDir={sortDir} onSort={handleSort}
            filters={filters} onFilterChange={f => { setFilters(f); setPage(1); }}
            loading={dataLoading} showAddNew={false} testId="user-mgmt-grid"
            exportFileName="User_Management" onBack={onBack}
            onEdit={(row) => setEditUserModal(row)}
            cellRenderer={(field, value, row) => {
              if (field === 'Status') return <span style={{ fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 3, background: value === 'S' ? '#e8f8ef' : '#fdeceb', color: value === 'S' ? '#27ae60' : '#e74c3c' }}>{value === 'S' ? 'Active' : 'Inactive'}</span>;
              if (field === 'LoginStatusDisplay') return <span style={{ fontSize: 10, fontWeight: 600, padding: '1px 6px', borderRadius: 3, background: value === 'Login' ? '#dbeafe' : value === 'Locked' ? '#fef2f2' : '#f3f4f6', color: value === 'Login' ? '#1d4ed8' : value === 'Locked' ? '#dc2626' : '#6b7280' }}>{value}</span>;
              if (field === 'LevelDisplay' && row) return (
                <span title={row.LevelTooltip} style={{ fontSize: 10, cursor: 'help', display: 'flex', alignItems: 'center', gap: 3 }}>
                  {value !== '-' && <Info size={10} color="#6b7280" />}
                  {value}
                </span>
              );
              return null;
            }}
          />
        </div>
      </div>

      {/* Modals */}
      {ctxMenu && <ContextMenu x={ctxMenu.x} y={ctxMenu.y} items={ctxMenu.items} onClose={() => setCtxMenu(null)} />}
      {editNodeModal && <EditNodeModal node={editNodeModal} onClose={() => setEditNodeModal(null)} onSave={editNodeModal.type === 'group' ? editGroup : editRole} />}
      {editUserModal && <EditUserModal user={editUserModal} onClose={() => setEditUserModal(null)} onSave={editUser} groups={groups} roles={roles} />}
    </div>
    </FormHelp>
  );
};

/* ═══════════════════════════════════════════════════
   WRAPPER
═══════════════════════════════════════════════════ */
const UserManagementScreen = ({ theme = 'light', onBack }) => (
  <UserTreeProvider>
    <UserManagementInner onBack={onBack} />
  </UserTreeProvider>
);

export default UserManagementScreen;
